/**
 * lib/sync/syncLock.ts
 *
 * ESCALA 0B.1 (P2#7) / 0B Fase B — Serialización de corridas de sync por SCOPE entre
 * pestañas. Una sola pestaña ejecuta por `scope`; la otra observa (no encola: evita
 * pilas de corridas redundantes). Prefiere Web Locks API; fallback a un lease en Dexie
 * app_meta con TTL para navegadores sin Web Locks.
 *
 * ESCALA 0B Fase B (concurrencia dura):
 *   • HEARTBEAT: el lease del fallback se RENUEVA periódicamente mientras la pestaña
 *     dueña vive (antes: TTL fijo de 60s → un sync largo lo dejaba expirar y otra
 *     pestaña entraba concurrente, reintroduciendo P2#7). La renovación solo ocurre si
 *     seguimos siendo dueños; si perdimos el lease, se detiene.
 *   • PROPIEDAD por página: `fn` recibe un LockHandle con `isOwner()`; el paginador lo
 *     consulta antes de cada página y ABORTA (interrupted, sin promover) si perdió la
 *     propiedad → una pestaña que ya no es dueña nunca corrompe sync_runs.
 *   • RECUPERACIÓN: si la pestaña dueña MUERE, deja de renovar; el lease expira y otra
 *     pestaña lo adquiere y completa. Con persistencia atómica (noResume), la pestaña
 *     muerta no promovió nada parcial → checkpoint intacto.
 *
 * NO sustituye la idempotencia del motor: la complementa evitando escrituras
 * concurrentes al mismo estado de corrida.
 */
import { db } from '@lib/dexie/schema'

const DEFAULT_LEASE_MS = 60_000
// Renovar a ~1/3 del TTL da 2 latidos de margen antes de expirar ante un GC/jank.
const DEFAULT_HEARTBEAT_DIVISOR = 3

/** Comprobador de propiedad del lock, pasado a `fn` para chequear entre páginas. */
export interface LockHandle {
  /** true si esta pestaña sigue siendo la dueña vigente del lock. */
  isOwner: () => Promise<boolean>
}

interface LockNavigator {
  locks?: {
    request: (
      name: string,
      opts: { ifAvailable?: boolean },
      cb: (lock: unknown) => Promise<unknown>
    ) => Promise<unknown>
  }
}

/** Handle trivial para el path de Web Locks: el navegador garantiza exclusividad
 * durante toda la callback, así que la propiedad nunca se pierde a media corrida. */
const ALWAYS_OWNER: LockHandle = { isOwner: async () => true }

/**
 * Ejecuta `fn` bajo un lock exclusivo por `key`. Si otra pestaña ya lo tiene,
 * NO ejecuta y devuelve `{ ran: false }` (la otra pestaña está sincronizando).
 * `fn` recibe un LockHandle para comprobar propiedad entre unidades de trabajo.
 */
export async function withScopeLock<T>(
  key: string,
  fn: (handle: LockHandle) => Promise<T>,
  opts?: { leaseMs?: number; heartbeatMs?: number }
): Promise<{ ran: true; value: T } | { ran: false }> {
  const lockName = `sync:${key}`
  const nav = (typeof navigator !== 'undefined' ? navigator : undefined) as
    | (Navigator & LockNavigator)
    | undefined

  // ── Web Locks API (preferido) ──────────────────────────────────────────────
  // El navegador mantiene el lock EXCLUSIVO mientras la promesa de la callback esté
  // pendiente y lo libera automáticamente si la pestaña muere → sin heartbeat ni fugas.
  if (nav?.locks?.request) {
    let ran = false
    let value: T | undefined
    await nav.locks.request(lockName, { ifAvailable: true }, async lock => {
      if (!lock) return // otra pestaña ejecuta → observar
      ran = true
      value = await fn(ALWAYS_OWNER)
    })
    return ran ? { ran: true, value: value as T } : { ran: false }
  }

  // ── Fallback: lease en Dexie app_meta con TTL + HEARTBEAT + recuperación ──────
  const leaseMs = opts?.leaseMs ?? DEFAULT_LEASE_MS
  const heartbeatMs = opts?.heartbeatMs ?? Math.floor(leaseMs / DEFAULT_HEARTBEAT_DIVISOR)
  const metaKey = `lock:${lockName}`
  const owner = crypto.randomUUID()

  const acquired = await db.transaction('rw', db.app_meta, async () => {
    const cur = await db.app_meta.get(metaKey)
    const now = Date.now()
    if (cur) {
      const exp = Number(cur.value.split('|')[1] ?? 0)
      if (exp > now) return false // lease vigente de otra pestaña
      // lease expirado (dueño anterior murió) → recuperamos.
    }
    await db.app_meta.put({ key: metaKey, value: `${owner}|${now + leaseMs}`, updated_at: now })
    return true
  })
  if (!acquired) return { ran: false }

  // ¿Seguimos siendo el dueño vigente? (id coincide y no expiró). Base de isOwner y
  // de la renovación: nunca renovamos un lease que ya perdimos.
  const stillOwner = async (): Promise<boolean> => {
    const cur = await db.app_meta.get(metaKey).catch(() => undefined)
    if (!cur || !cur.value.startsWith(`${owner}|`)) return false
    return Number(cur.value.split('|')[1] ?? 0) > Date.now()
  }

  // HEARTBEAT: renueva el TTL mientras vivamos y sigamos siendo dueños. Si perdimos el
  // lease (otra pestaña lo robó tras una expiración por jank), deja de renovar → isOwner
  // pasará a false y el paginador abortará limpio.
  const heartbeat = setInterval(() => {
    void db
      .transaction('rw', db.app_meta, async () => {
        const cur = await db.app_meta.get(metaKey)
        if (!cur || !cur.value.startsWith(`${owner}|`)) return // ya no somos dueños
        await db.app_meta.put({
          key: metaKey,
          value: `${owner}|${Date.now() + leaseMs}`,
          updated_at: Date.now(),
        })
      })
      .catch(() => {})
  }, heartbeatMs)
  // No mantener vivo el proceso (Node) por el intervalo del heartbeat.
  ;(heartbeat as unknown as { unref?: () => void }).unref?.()

  try {
    const value = await fn({ isOwner: stillOwner })
    return { ran: true, value }
  } finally {
    clearInterval(heartbeat)
    const cur = await db.app_meta.get(metaKey).catch(() => undefined)
    // Solo borra el lease si sigue siendo NUESTRO (no pisar al que lo recuperó).
    if (cur && cur.value.startsWith(`${owner}|`)) await db.app_meta.delete(metaKey).catch(() => {})
  }
}
